import { useCallback } from "react";
import { v4 as uuidv4, validate as validateUuid } from "uuid";

import { faro } from "@grafana/faro-react";
import { useStore } from "@nanostores/react";
import { useTrackAnalyticsEvent } from "@vega-api/generated/analytics-events/analytics-events";
import type { AnalyticsEventAttribute } from "@vega-api/generated/types/analyticsEventAttribute";
import { type AnalyticsEventAttributeAttributeName as AnalyticsEventAttributeAttributeNameType } from "@vega-api/generated/types/analyticsEventAttributeAttributeName";
import { AnalyticsEventAttributeAttributeType } from "@vega-api/generated/types/analyticsEventAttributeAttributeType";
import type { AnalyticsEventDataItem } from "@vega-api/generated/types/analyticsEventDataItem";
import { AnalyticsEventDataItemDataType } from "@vega-api/generated/types/analyticsEventDataItemDataType";
import { type AnalyticsEventDataItemName } from "@vega-api/generated/types/analyticsEventDataItemName";
import type { Uuid } from "@vega-api/generated/types/uuid";
import { getEventOrigin, isDev, isProd, isUat } from "@vega-core/utils/global";

import { useSession } from "../SessionContext/SessionContext";
import { $selectedInvestorId } from "../stores/investors.store";

export type AnalyticsAttributes =
  | Partial<
      Record<
        AnalyticsEventAttributeAttributeNameType,
        boolean | number | string | null | undefined
      >
    >
  | Record<string, boolean | number | string | null | undefined>;

export type AnalyticsDataItems = Partial<
  Record<string, boolean | number | string | null | undefined>
>;

type UseAnalyticsReturn = {
  trackEvent: {
    (eventName: string, attributes?: AnalyticsAttributes): void;
    (
      eventName: string,
      attributes: AnalyticsAttributes | undefined,
      dataItems: AnalyticsDataItems,
    ): void;
  };
};

export function useAnalytics(): UseAnalyticsReturn {
  const { user } = useSession();
  const selectedInvestorId = useStore($selectedInvestorId);
  const { mutateAsync: trackAnalyticsEvent } = useTrackAnalyticsEvent({
    mutation: {
      retry: 3,
      retryDelay: 1000,
    },
  });

  const trackEvent = useCallback(
    (
      eventName: string,
      attributes?: AnalyticsAttributes,
      dataItems?: AnalyticsDataItems,
    ) => {
      if (!user) {
        return;
      }

      try {
        const enrichedAttributes: AnalyticsAttributes = {
          ...attributes,
          eventOrigin: getEventOrigin(),
          eventTimestamp: new Date().toISOString(),
          hostname: window.location.hostname,
          userId: user.id,
          ...(user.extContactType && { userContactType: user.extContactType }),
          ...(user.extEligibility && {
            userEligibilityType: user.extEligibility,
          }),
          ...(selectedInvestorId != null && { investorId: selectedInvestorId }),
        };

        const eventAttributes: AnalyticsEventAttribute[] = (
          Object.entries(enrichedAttributes) as [
            AnalyticsEventAttributeAttributeNameType,
            boolean | number | string | null | undefined,
          ][]
        )
          .filter(([, value]) => value != null)
          .map(([key, value]) => ({
            attributeName: key,
            attributeType: getAttributeType(value),
            value: String(value),
          }));

        // Build data items array from provided data
        const eventDataItems: AnalyticsEventDataItem[] = [];

        if (dataItems) {
          Object.entries(dataItems).forEach(([name, value]) => {
            if (value != null) {
              eventDataItems.push({
                name: name as AnalyticsEventDataItemName,
                dataType: getDataItemType(value),
                value: String(value),
              });
            }
          });
        }

        const eventId = uuidv4() as Uuid;

        trackAnalyticsEvent({
          data: {
            eventId,
            eventName,
            // generalPartnerId, // TODO: Enable when backend is ready
            eventAttributes,
            ...(eventDataItems.length > 0 && { data: eventDataItems }),
          },
        }).catch((error) => {
          if (isDev) {
            console.warn(
              `[Analytics] Failed after retries: ${eventName}`,
              error,
            );
          }

          if (isUat || isProd) {
            faro.api.pushError(error as Error, {
              type: "analytics_tracking_failed",
              context: {
                eventName,
                eventId,
                attributeCount: String(eventAttributes.length),
              },
            });
          }
        });
      } catch (error) {
        if (isDev) {
          console.warn(`[Analytics] Exception: ${eventName}`, error);
        }

        if (isUat || isProd) {
          faro.api.pushError(error as Error, {
            type: "analytics_exception",
            context: {
              eventName,
              attributes: JSON.stringify(attributes),
            },
          });
        }
      }
    },
    [trackAnalyticsEvent, user, selectedInvestorId],
  ) as UseAnalyticsReturn["trackEvent"];

  return { trackEvent };
}

function getAttributeType(
  value: boolean | number | string | null | undefined,
): AnalyticsEventAttributeAttributeType {
  if (typeof value === "number") {
    return Number.isInteger(value)
      ? AnalyticsEventAttributeAttributeType.INT
      : AnalyticsEventAttributeAttributeType.DOUBLE;
  }

  return AnalyticsEventAttributeAttributeType.STRING;
}

function getDataItemType(
  value: boolean | number | string | null | undefined,
): (typeof AnalyticsEventDataItemDataType)[keyof typeof AnalyticsEventDataItemDataType] {
  if (typeof value === "number") {
    return Number.isInteger(value)
      ? AnalyticsEventDataItemDataType.INT
      : AnalyticsEventDataItemDataType.DOUBLE;
  }

  if (typeof value === "string" && validateUuid(value)) {
    return AnalyticsEventDataItemDataType.UUID;
  }

  return AnalyticsEventDataItemDataType.STRING;
}
